import { useState } from 'react';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { cn } from '@/lib/core/utils/cn';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from '@/components/ui/radio-group';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { Icon } from '@/components/ui/icon';
import { getEventDetail } from '@/services/platform/data/events';
import type { EventWhereSheetProps } from '../types';
import { deriveBookingUpdatesFromEvent, deriveVenueSelectionUpdates, whereOptionKey } from '../derive';

/**
 * "Where" step — lists this event's own occurrence venues (contract §3.1.1: an event can run
 * different dates at different venues) plus, when the event belongs to a group (§3.0), a row
 * per sibling edition (each with its own venue/menu/price/description). Picking an occurrence
 * venue only narrows the current event; picking a sibling switches `booking.event` to a
 * DIFFERENT `EventDetailDto` (a fetch). If that sibling is itself multi-venue, the sheet stays
 * open and re-renders with its occurrence venues instead of closing on a guess.
 */
export function EventWhereSheet({
  isOpen,
  onClose,
  cmsProps,
  booking,
  onBookingChange,
  options,
  isLoadingOptions,
}: EventWhereSheetProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const currentKey = whereOptionKey(booking.event?.id, booking.venue?.venueNodeKey, booking.venue?.name);

  async function handleValueChange(key: string) {
    const picked = options.find(option => option.key === key);
    if (!picked || !booking.event || pendingKey) return;

    if (picked.kind === 'occurrence') {
      onBookingChange(deriveVenueSelectionUpdates(booking.event, picked));
      onClose();
      return;
    }

    if (!picked.eventId) return;
    setPendingKey(key);
    try {
      const detail = await getEventDetail(picked.eventId, locale);
      if (!detail) return;
      const updates = deriveBookingUpdatesFromEvent(detail);
      onBookingChange(updates);
      // A multi-venue sibling leaves `venue: null` (see `deriveBookingUpdatesFromEvent`) — stay
      // open so the sheet re-renders with that event's own occurrence venues instead of a guess.
      if (updates.venue) onClose();
    } finally {
      setPendingKey(null);
    }
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="h-[80dvh] min-h-[min(650px,100dvh)]">
        <DrawerHeader className="shrink-0 gap-2 pt-8">
          <div className="flex items-center gap-8">
            <DrawerTitle className="flex-1 text-[32px] font-medium tracking-[0.5px]">
              {cmsProps?.title ?? t('App.Labels.Where')}
            </DrawerTitle>
            <DrawerClose
              className="flex size-11 cursor-pointer items-center justify-center rounded-full"
              aria-label="Close"
            >
              <Icon
                name="xmark"
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-jncquoi-neutral-1000"
              />
            </DrawerClose>
          </div>
          {cmsProps?.subtitle && (
            <DrawerDescription className="text-[16px] text-[#707070]">{cmsProps.subtitle}</DrawerDescription>
          )}
        </DrawerHeader>

        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto px-4 pb-10">
          {isLoadingOptions && options.length === 0 ? (
            <SpinLoading wrapperClassName="py-10" />
          ) : (
            <RadioGroup value={currentKey} onValueChange={handleValueChange} className="flex flex-col">
              {options.map(option => {
                const itemId = `event-where-${option.key}`;
                const isPending = pendingKey === option.key;
                const disabled = !!option.disabled || (!!pendingKey && !isPending);
                return (
                  <RadioGroupLabel
                    key={option.key}
                    htmlFor={itemId}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-3 border-b-[0.5px] border-[#e1e1e1] py-4 text-[16px] tracking-normal text-[#1a1a1a]',
                      disabled && 'pointer-events-none',
                      isPending && 'opacity-40'
                    )}
                  >
                    <RadioGroupItem id={itemId} value={option.key} disabled={disabled} />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="font-pp-neue-montreal text-[16px] font-medium">
                        {option.venueName}
                      </span>
                      {option.detail && (
                        <span className="font-pp-neue-montreal text-[12px] text-[#707070]">
                          {option.detail}
                        </span>
                      )}
                    </div>
                  </RadioGroupLabel>
                );
              })}
            </RadioGroup>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
